import React, { useState, useEffect } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/tables/Table';
import { useToast } from '@/contexts/ToastContext';
import { useLogger } from '@/contexts/LogContext';
import { RosterMember, LogLevel, LogCategory, UserRole } from '@/types';
import { Button } from '@/components/ui/Button';
import ProfileModal from '@/components/modals/ProfileModal';
import { API_BASE_URL } from '@/config';
import mockRosterData from '../../../public/roster-members.json';

const RosterRealDataTable = ({ currentUserId }: { currentUserId: string }) => {
  const [roster, setRoster] = useState<RosterMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedMember, setSelectedMember] = useState<RosterMember | null>(null);
  const { notify } = useToast();
  const { addLog } = useLogger();

  useEffect(() => {
    const fetchRosterData = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(`${API_BASE_URL}/api/roster`);
        if (!response.ok) {
          throw new Error(`Failed to fetch roster members with status: ${response.status}`);
        }
        const data: RosterMember[] = await response.json();
        setRoster(data);
        addLog('Successfully fetched roster members from API', LogLevel.INFO, LogCategory.API);
      } catch (apiError) {
        addLog(`Roster API fetch failed: ${(apiError as Error).message}. Falling back to imported mock data.`, LogLevel.WARN, LogCategory.API);
        notify('Live roster data unavailable. Displaying static fallback data.', 'warn');
        setRoster(
          (mockRosterData as RosterMember[]).map((member) => ({
            ...member,
            role: member.role as UserRole,
            assets: { ips: [], contents: [], events: [], products: [], services: [], equipment: [], instruments: [], tickets: [] },
            subscriberOnly: {
              email: member.subscriberOnly?.email || 'Not Available',
              phone: 'Not Available',
              agentContact: 'Not Available',
            },
          }))
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchRosterData();
  }, [addLog, notify]);

  const handleViewProfile = (member: RosterMember) => {
    setSelectedMember(member);
  };

  const handleCloseModal = () => {
    setSelectedMember(null);
  };

  const handleChat = () => {
    notify('Chat feature is not yet implemented.', 'info');
  };

  const handleBook = () => {
    notify('Booking feature is not yet implemented.', 'info');
  };

  if (isLoading && roster.length === 0) {
    return <div className="text-center p-8">Loading Roster...</div>;
  }

  return (
    <div>
      <div className="border rounded-lg overflow-hidden border-kala-700">
        <Table>
          <TableHeader>
            <TableRow className="bg-kala-900 hover:bg-kala-900">
              <TableHead className="text-white">Artist/Member</TableHead>
              <TableHead className="text-white">Role</TableHead>
              <TableHead className="text-white">Location</TableHead>
              <TableHead className="text-white">Status</TableHead>
              <TableHead className="text-white"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {roster.map((member) => (
              <TableRow key={member.id} className="border-kala-800 hover:bg-kala-800/50">
                <TableCell className="flex items-center gap-3">
                  <img src={member.avatar} alt={member.name} className="w-10 h-10 rounded-full object-cover" />
                  <span className="font-bold">{member.name}</span>
                </TableCell>
                <TableCell>{member.role}</TableCell>
                <TableCell>{member.location}</TableCell>
                <TableCell>
                  <span className={`text-xs font-semibold px-2 py-1 rounded-full ${member.status === 'active' ? 'bg-green-500/10 text-green-400' : 'bg-yellow-500/10 text-yellow-400'}`}>
                    {member.status}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="outline" size="sm" onClick={() => handleViewProfile(member)}>
                    View Profile
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {selectedMember && (
        <ProfileModal
          member={selectedMember}
          onClose={handleCloseModal}
          onChat={() => handleChat()} // Placeholder
          onBook={() => handleBook()} // Placeholder
          isOwnProfile={selectedMember.id === currentUserId}
        />
      )}
    </div>
  );
};

export default RosterRealDataTable;
